import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import ClimateInYourArea from './ClimateInYourArea/ClimateInYourArea';
import RiskMappedOntoHouse from './RiskMappedOntoHouse/RiskMappedOntoHouse';
import Budgeting from './Budgeting/Budgeting';
import RecurringBills from './RecurringBills/RecurringBills';
import WhichRisksToFocusOn from './WhichRisksToFocusOn/WhichRisksToFocusOn';
import PuttingItAllTogether from './PuttingItAllTogether/PuttingItAllTogether';
import LTV from './LTV/LTV';
import HowToThinkAbout from './HowToThinkAbout/HowToThinkAbout';
// import FEMAData from './06037.json';
import { AreaDataContext } from '../PropertyExplorer';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import WhatWillRiskCost from './WhatWillRiskCost/WhatWillRiskCost';

import RiskOverviewSectionLoadingState from '/src/Components/RiskOverviewSectionLoadingState/RiskOverviewSectionLoadingState';
import './style.css';

export const RiskOverviewContext = createContext();

const RiskOverview = ({ propertyData }) => {

    const { deviceType } = useContext(WindowSizeContext);
    const { geoData } = useContext(AreaDataContext);
    const { handleSectionChange, handleUrlSubsectionChange } = useContext(AreaDataContext);
    const { portalData } = useContext(AddressSearchContext);
    const fipsData = geoData?.fips
    const fips = propertyData?.data?.property_details?.fips
    
    // Load the qgd data from local storage
    
    const femaData = fipsData?.fema_data || {};
    
    // Create refs outside of the sections array
    const howToThinkAboutRef = useRef(null);
    const whatIsHappeningRef = useRef(null);
    const whichRisksToFocusOnRef = useRef(null);
    const whatWillRiskCostRef = useRef(null);
    const whereSusceptibleRef = useRef(null);
    const budgetingRef = useRef(null);
    const recurringBillsRef = useRef(null);
    const longTermValueRef = useRef(null);
    const puttingItAllTogetherRef = useRef(null);
    // Add a ref for the scrolling container
    const scrollContainerRef = useRef(null);
    
    const street_address = propertyData?.data?.property_details?.street || "this home"
    
    // Set the titles for each section
    const howToThinkAboutMenuTitle = "How to Think About Climate Risk"
    const howToThinkAboutTitle = "Climate Risk is Financial Risk for Homeowners"
    const howToThinkAboutSlug = "how-to-think-about"
    
    const whatIsHappeningMenuTitle = "Climate Risks In Your Area"
    const whatIsHappeningTitle = "What is the climate risk profile of your area?"
    const whatIsHappeningSlug = "climate-risks-in-your-area"
    
    const whichRisksToFocusOnMenuTitle = "Which Risks to Focus On"
    const whichRisksToFocusOnTitle = `Which risks should you focus on for ${street_address}?`
    const whichRisksToFocusOnSlug = "which-risks-to-focus-on"
    
    const whatWillRiskCostMenuTitle = "$$$ What Will Risk Cost?"
    const whatWillRiskCostTitle = "What will climate risk cost you?"
    const whatWillRiskCostSlug = "what-will-risk-cost"
    
    const budgetingMenuTitle = "$$$ Sudden Costs"
    const budgetingTitle = "What kind of sudden costs might that exposure create?"
    const budgetingSlug = "sudden-costs"
    
    const recurringBillsMenuTitle = "$$$ Recurring Bills"
    const recurringBillsTitle = "How will climate exposure affect your recurring bills?"
    const recurringBillsSlug = "recurring-bills"
    
    const longTermValueMenuTitle = "$$$ Long-Term Value"
    const longTermValueTitle = "How will climate exposure affect the long-term value of this home?"
    const longTermValueSlug = "long-term-value"
    
    const puttingItAllTogetherMenuTitle = "How Worried Should I Be?"
    const puttingItAllTogetherTitle = "So How Worried Should I Be About All of This?"
    const puttingItAllTogetherSlug = "how-worried-should-i-be"
    
    // Keep sections array for navigation only
    const sections = [
        { id: howToThinkAboutSlug, title: howToThinkAboutTitle, menuTitle: howToThinkAboutMenuTitle },
        { id: whatIsHappeningSlug, title: whatIsHappeningTitle, menuTitle: whatIsHappeningMenuTitle },
        { id: whichRisksToFocusOnSlug, title: whichRisksToFocusOnTitle, menuTitle: whichRisksToFocusOnMenuTitle },
        // { id: whatWillRiskCostSlug, title: whatWillRiskCostTitle, menuTitle: whatWillRiskCostMenuTitle },
        { id: budgetingSlug, title: budgetingTitle, menuTitle: budgetingMenuTitle },
        { id: recurringBillsSlug, title: recurringBillsTitle, menuTitle: recurringBillsMenuTitle },
        { id: longTermValueSlug, title: longTermValueTitle, menuTitle: longTermValueMenuTitle },
        { id: puttingItAllTogetherSlug, title: puttingItAllTogetherTitle, menuTitle: puttingItAllTogetherMenuTitle }
    ];
    
    const [activeSection, setActiveSection] = useState(howToThinkAboutSlug);
    const [completedSections, setCompletedSections] = useState(new Set());

    const [financialSectionSelection, setFinancialSectionSelection] = useState(null);

    // New useEffect to manage completedSections based on activeSection
    useEffect(() => {
        handleSectionChange('subsection', activeSection);
        handleUrlSubsectionChange(activeSection);
        const currentIndex = sections.findIndex(section => section.id === activeSection);
        const newCompleted = new Set();

        // Add all sections before the current one to completed
        sections.forEach((section, index) => {
            if (index < currentIndex) {
                newCompleted.add(section.id);
            }
        });

        setCompletedSections(newCompleted);
    }, [activeSection]); // Only depend on activeSection

    // Intersection Observer useEffect
    useEffect(() => {
        let observer = null;
        
        // Clean up any existing observation
        if (!propertyData || !geoData) {
            if (observer) {
                observer.disconnect();
            }
            return;
        }

        console.log('Setting up intersection observer');
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            {
                threshold: [0.3],
                rootMargin: '-75px 0px -75px 0px'
            }
        );

        const elementsToObserve = [
            whatIsHappeningRef.current,
            whichRisksToFocusOnRef.current,
            budgetingRef.current,
            recurringBillsRef.current,
            longTermValueRef.current,
            puttingItAllTogetherRef.current
        ];

        elementsToObserve.forEach(element => {
            if (element) {
                observer.observe(element);
            }
        });

        return () => {
            if (observer) {
                observer.disconnect();
                console.log('Observer disconnected during cleanup');
            }
        };
    }, [propertyData, geoData]);

    // Scroll detection useEffect
    useEffect(() => {
        let contentElement = null;
        
        // Clean up any existing scroll listeners
        if (!propertyData || !geoData) {
            if (contentElement) {
                contentElement.removeEventListener('scroll', handleScroll);
            }
            return;
        }

        console.log('Setting up scroll detection');
        contentElement = document.querySelector('.risk-overview-content');

        if (!contentElement) {
            console.error('No content container found!');
            return;
        }

        const handleScroll = () => {
            console.log('SCROLL DETECTED!', contentElement.scrollTop);
        };

        contentElement.addEventListener('scroll', handleScroll);
        console.log('Scroll listener added to content container');

        return () => {
            if (contentElement) {
                contentElement.removeEventListener('scroll', handleScroll);
                console.log('Scroll listener removed during cleanup');
            }
        };
    }, [propertyData, geoData]);

    return (
        <RiskOverviewContext.Provider value={{
            femaData,
            financialSectionSelection,
            setFinancialSectionSelection
        }}>
            <div className="risk-overview-container">
                {deviceType !== 'mobile' && (
                    <nav className="risk-overview-nav">
                        <ul className="risk-overview-nav-list">
                        {sections.map(section => (
                            <li
                                key={section.id}
                                className={`risk-overview-nav-item ${activeSection === section.id ? 'active' : ''
                                    } ${completedSections.has(section.id) ? 'completed' : ''}`}
                                onClick={() => document.getElementById(section.id).scrollIntoView({ behavior: 'smooth' })}
                            >
                                {section.menuTitle}
                            </li>
                        ))}
                        </ul>
                    </nav>
                )}

                <div
                    className="risk-overview-content"
                    ref={scrollContainerRef}
                >
                    <>
                        <section id={howToThinkAboutSlug} ref={howToThinkAboutRef} className="risk-overview-section">
                            <HowToThinkAbout propertyData={propertyData} title={howToThinkAboutTitle} />
                        </section>
                        {(!propertyData || !geoData || !portalData) && (
                            <>
                                <RiskOverviewSectionLoadingState title={whatIsHappeningTitle} />
                                <RiskOverviewSectionLoadingState title={whichRisksToFocusOnTitle} />
                                {/* <RiskOverviewSectionLoadingState title={whatWillRiskCostTitle} /> */}
                                <RiskOverviewSectionLoadingState title={budgetingTitle} />
                                <RiskOverviewSectionLoadingState title={recurringBillsTitle} />
                                <RiskOverviewSectionLoadingState title={longTermValueTitle} />
                                <RiskOverviewSectionLoadingState title={puttingItAllTogetherTitle} />
                            </>
                        )}
                        {(propertyData && geoData) && (
                            <>

                                <section id={whatIsHappeningSlug} ref={whatIsHappeningRef} className="risk-overview-section">
                                    <ClimateInYourArea propertyData={propertyData} title={whatIsHappeningTitle} />
                                </section>

                                <section id={whichRisksToFocusOnSlug} ref={whichRisksToFocusOnRef} className="risk-overview-section">
                                    <WhichRisksToFocusOn propertyData={propertyData} title={whichRisksToFocusOnTitle} />
                                </section>
{/* 
                                <section id={whatWillRiskCostSlug} ref={whatWillRiskCostRef} className="risk-overview-section">
                                    <WhatWillRiskCost propertyData={propertyData} title={whatWillRiskCostTitle} />
                                </section> */}

                                <section id={budgetingSlug} ref={budgetingRef} className="risk-overview-section">
                                    <Budgeting propertyData={propertyData} title={budgetingTitle} />
                                </section>

                                <section id={recurringBillsSlug} ref={recurringBillsRef} className="risk-overview-section">
                                    <RecurringBills propertyData={propertyData} title={recurringBillsTitle} />
                                </section>

                                <section id={longTermValueSlug} ref={longTermValueRef} className="risk-overview-section">
                                    <LTV propertyData={propertyData} title={longTermValueTitle} femaData={femaData} />
                                </section>

                                <section id={puttingItAllTogetherSlug} ref={puttingItAllTogetherRef} className="risk-overview-section">
                                    <PuttingItAllTogether propertyData={propertyData} title={puttingItAllTogetherTitle} />
                                </section>
                            </>
                        )}
                    </>
                </div>
            </div>
        </RiskOverviewContext.Provider>
    );
};

export default RiskOverview;